/* CommunitySection — homepage block directly below Reviews.
   Real, current-day, published questions only (server-filtered). */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircleQuestion, Sunrise, Sparkles } from 'lucide-react';
import { api, onDayChange, useCommunitySession } from '../../lib/communityApi';
import QuestionCard from './QuestionCard';
import AllowanceBar from './AllowanceBar';
import { topicStyle } from './topicStyles';

const CommunitySection = () => {
  const { session, config, refresh } = useCommunitySession();
  const [items, setItems] = useState(null);
  const [serverTime, setServerTime] = useState(null);

  const load = useCallback(() => {
    api.list({ sort: 'newest', page: 1 }).then((d) => { setItems(d.items.slice(0, config?.content?.homepage?.display_limit || 6)); setServerTime(d.server_time); }).catch(() => setItems([]));
  }, [config]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => onDayChange(load), [load]);

  const h = config?.content?.homepage;
  if (config && config.settings?.community_enabled === false) return null;

  return (
    <section className="relative py-20 md:py-24 px-4 md:px-6 overflow-hidden bg-orange-50" data-testid="community-section" aria-labelledby="community-heading">
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-40 blur-3xl bg-orange-300" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 -right-24 w-80 h-80 rounded-full opacity-40 blur-3xl" style={{ background: '#A5B4FC' }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 rounded-full opacity-40 blur-3xl" style={{ background: '#6EE7B7' }} />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-start">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-widest uppercase mb-3 inline-flex items-center gap-2" style={{ color: '#C2410C' }}>
              <Sparkles size={14} /> {h?.eyebrow || 'LEARNIXTA COMMUNITY'}
            </div>
            <h2 id="community-heading" className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight tracking-tight" data-testid="community-heading">
              {(h?.heading || 'Ask Questions. Share Experience. Find Clarity.').split('. ').map((part, i, arr) => (
                <span key={i} className={i === 1 ? 'text-teal-600' : i === 2 ? 'text-indigo-600' : ''}>{part}{i < arr.length - 1 ? '. ' : ''}<br className="hidden md:block" /></span>
              ))}
            </h2>
            <p className="mt-4 text-base text-gray-700" data-testid="community-description">{h?.description}</p>
            <p className="mt-3 text-sm text-gray-700 bg-white/70 border border-amber-100 rounded-2xl px-4 py-3 inline-flex items-start gap-2" data-testid="community-rule-summary">
              <Sunrise size={16} className="text-amber-500 mt-0.5 shrink-0" /> {h?.rule_summary}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/community?ask=1" data-testid="community-ask-cta"
                className="inline-flex items-center gap-2 text-white font-extrabold px-6 py-3 rounded-full transition-[transform,box-shadow] hover:shadow-lg active:scale-95 min-h-[44px]"
                style={{ background: 'linear-gradient(90deg,#0D9488,#2563EB)' }}>
                <MessageCircleQuestion size={16} /> {h?.primary_cta || 'Ask a question'}
              </Link>
              <Link to="/community" data-testid="community-explore-cta"
                className="inline-flex items-center gap-2 font-extrabold px-6 py-3 rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors min-h-[44px]">
                {h?.secondary_cta || 'Explore the community'} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-1.5" aria-label="Topics">
              {(config?.topics || []).map((t) => { const st = topicStyle(t.id); return (
                <Link key={t.id} to={`/community?topic=${t.id}`} className="text-[11px] font-bold rounded-full px-2.5 py-1 hover:opacity-80 transition-opacity" style={{ background: st.soft, color: st.color }} data-testid={`community-topic-chip-${t.id}`}>{t.label}</Link>
              ); })}
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur rounded-3xl border border-amber-100 shadow-sm p-4 md:p-5" data-testid="community-allowance-card">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Your allowances today</p>
            {session ? <AllowanceBar session={session} /> : <p className="text-sm text-gray-500">Loading your allowances…</p>}
          </div>
        </div>

        <div className="mt-12">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <h3 className="text-lg md:text-xl font-extrabold text-gray-900" data-testid="community-recent-heading">{h?.recent_questions_heading || "Today's community questions"}</h3>
            {items && items.length > 0 && <Link to="/community" className="text-sm font-semibold text-teal-700 hover:underline" data-testid="community-see-all">See all today's questions →</Link>}
          </div>
          {items === null && <p className="mt-4 text-sm text-gray-500">{config?.content?.directory?.loading || 'Loading community questions…'}</p>}
          {items && items.length === 0 && (
            <div className="mt-4 bg-white rounded-3xl border-2 border-dashed border-amber-200 p-8 text-center max-w-2xl" data-testid="community-empty-state">
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center text-white mb-3" style={{ background: 'linear-gradient(135deg,#F59E0B,#EC4899)' }}><Sunrise size={26} /></div>
              <h4 className="text-xl font-extrabold text-gray-900">{h?.empty_heading}</h4>
              <p className="text-sm text-gray-600 mt-1.5 max-w-md mx-auto">{h?.empty_description}</p>
              <Link to="/community?ask=1" className="mt-4 inline-flex items-center gap-2 text-white font-extrabold px-6 py-3 rounded-full min-h-[44px]" style={{ background: 'linear-gradient(90deg,#F59E0B,#EC4899)' }} data-testid="community-empty-cta">
                {h?.empty_cta || "Ask today's first question"} <ArrowRight size={15} />
              </Link>
            </div>
          )}
          {items && items.length > 0 && (
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-home-grid">
              {items.map((q) => <QuestionCard key={q.id} q={q} config={config} serverTime={serverTime} compact />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CommunitySection;
